import type { DropEvent, FileDropItem } from "react-aria";

import { DropZone } from "react-aria-components";

import { toast } from "@/components/toast/toast-queue";

import type { FileUploadDropZoneProps } from "../types";

// Imported from the module rather than the package barrel: the barrel also
// exports the file lists, which pull in the PDF viewer, so going through it
// would load pdfjs anywhere a drop zone is used.
import { useFileUploaderContext } from "../UseFileUploaderContext";

function toFileList(files: File[]): FileList {
  const dt = new DataTransfer();

  for (const f of files) dt.items.add(f);

  return dt.files;
}

export function FileUploadDropZone({
  children,
  className,
}: FileUploadDropZoneProps) {
  const { acceptedFileTypes, addFiles } = useFileUploaderContext();

  const handleFileDrop = async (e: DropEvent) => {
    const fileItems = e.items.filter(
      (item): item is FileDropItem => item.kind === "file",
    );

    if (fileItems.length === 0) return;

    const resolved = await Promise.all(fileItems.map((item) => item.getFile()));
    const droppedFiles = resolved.filter((f): f is File => f !== null);

    if (!acceptedFileTypes?.length) {
      addFiles(toFileList(droppedFiles));

      return;
    }

    const accepted = droppedFiles.filter((file) => {
      const ext = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;

      return acceptedFileTypes.some(
        (type) =>
          type.toLowerCase() === ext ||
          file.type.startsWith(type.replace("*", "")),
      );
    });

    if (accepted.length > 0) addFiles(toFileList(accepted));

    const rejectedCount = droppedFiles.length - accepted.length;

    if (rejectedCount > 0) {
      toast.add({
        description: `${rejectedCount} file${rejectedCount > 1 ? "s were" : " was"} not added. Accepted types: ${acceptedFileTypes.join(", ")}`,
        title: "File type not supported",
        type: "error",
      });
    }
  };

  return (
    <DropZone className={className} onDrop={handleFileDrop}>
      {children}
    </DropZone>
  );
}
